import './header.scss';
import Base from '../base/base';

interface HeaderOptions {
  /** Selector for the element that receives the compact class */
  selector?: string;
  /** Class toggled once the user scrolls past the threshold */
  compactClass?: string;
  /** Scroll distance in px before the compact class is applied */
  scrollThreshold?: number;
  debug?: boolean;
  [key: string]: any;
}

export default class Header extends Base {
  private config: {
    selector: string;
    compactClass: string;
    scrollThreshold: number;
  };

  private navContainer: HTMLElement | null = null;
  private isCompact: boolean = false;
  private isTicking: boolean = false;

  private boundOnScroll: () => void;

  constructor(options: HeaderOptions = {}) {
    // Pass the debug option to the Base class constructor
    super(options.debug || false);

    this.config = {
      selector: options.selector || '.nav-container',
      compactClass: options.compactClass || 'nav-compact',
      scrollThreshold: options.scrollThreshold ?? 75
    };

    this.boundOnScroll = this.onScroll.bind(this);

    this.init();
  }

  init(): void {
    this.log('Header initialized');

    this.navContainer = document.querySelector(this.config.selector);
    if (!this.navContainer) {
      this.log(`Header: "${this.config.selector}" not found, scroll behavior skipped`);
      return;
    }

    window.addEventListener('scroll', this.boundOnScroll, { passive: true });

    // Set the correct initial state in case the page loads already scrolled
    // (refresh mid-page, in-page anchors, browser scroll restoration).
    this.update();
  }

  /**
   * Scroll handler — rAF-gated so we collapse a burst of scroll events into a
   * single read/write per frame and avoid layout thrash.
   */
  private onScroll(): void {
    if (this.isTicking) return;
    this.isTicking = true;
    window.requestAnimationFrame(() => this.update());
  }

  /**
   * Toggle the compact class based on scroll position. The class is applied on
   * every viewport; the visual effect is scoped to desktop in the stylesheet,
   * so no breakpoint logic is needed here.
   */
  private update(): void {
    this.isTicking = false;
    if (!this.navContainer) return;

    const scrollY = window.scrollY || window.pageYOffset;
    const shouldCompact = scrollY > this.config.scrollThreshold;

    // Only touch the DOM when the state actually changes.
    if (shouldCompact === this.isCompact) return;

    this.isCompact = shouldCompact;
    this.navContainer.classList.toggle(this.config.compactClass, shouldCompact);
    this.log(`Header ${shouldCompact ? 'compacted' : 'expanded'} (scrollY=${scrollY})`);
  }

  /**
   * Cleanup method — detach the scroll listener.
   */
  destroy(): void {
    window.removeEventListener('scroll', this.boundOnScroll);
    this.navContainer = null;
  }
}
